import { useTranslation } from "react-i18next";

interface EngagementCardProps {
  totalSessions: number;
  completionRate: number;
  avgProgressionPercent: number | null;
  avgDuration: number | null;
  abandonedSessions: number;
  sessionsTrend?: {
    value: number;
    isPositive: boolean;
  };
}

/**
 * Small (i) icon + hover tooltip. Mirrors the pattern used in
 * CreateLearnerGroupModal.tsx so the visual language stays consistent across
 * StatsNew. Positioning is `bottom`: the tooltip appears below the label so
 * it doesn't clip out of the card on narrow viewports.
 */
const InfoTooltip = ({ text }: { text: string }) => (
  <span className="relative inline-flex items-center group">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-3.5 w-3.5 text-gray-400 cursor-help"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <span className="absolute left-1/2 top-full mt-1 -translate-x-1/2 z-50 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 pointer-events-none">
      <span className="block bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-56 shadow-lg whitespace-normal font-normal leading-snug">
        {text}
      </span>
    </span>
  </span>
);

/**
 * Hero card displaying engagement metrics (sessions, completion, duration)
 */
const EngagementCard = ({
  totalSessions,
  completionRate,
  avgProgressionPercent,
  avgDuration,
  abandonedSessions,
  sessionsTrend,
}: EngagementCardProps) => {
  const { t } = useTranslation();

  // Format duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs.toString().padStart(2, "0")}s`;
  };

  // Get completion color
  const getCompletionColor = (rate: number): string => {
    if (rate >= 80) return "bg-green-500";
    if (rate >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{t("statsNew.module.engagement")}</h3>
        <div className="p-2 bg-green-100 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
      </div>

      {/* Main metrics row */}
      <div className="grid grid-cols-2 gap-4 mb-3">
        {/* Sessions */}
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-gray-900">{totalSessions}</span>
            {sessionsTrend && (
              <span
                className={`text-sm font-medium ${
                  sessionsTrend.isPositive ? "text-green-600" : "text-red-600"
                }`}
              >
                {sessionsTrend.isPositive ? "+" : ""}
                {sessionsTrend.value}
              </span>
            )}
          </div>
          <span className="text-xs text-gray-500 inline-flex items-center gap-1">
            {t("statsNew.session.sessions")}
            <InfoTooltip text={t("statsNew.module.tooltips.sessions")} />
          </span>
        </div>

        {/* Completion & Progression */}
        <div className="flex flex-col gap-2">
          {/* Completion Rate */}
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${getCompletionColor(
                    completionRate,
                  )} transition-all duration-500`}
                  style={{ width: `${Math.min(completionRate, 100)}%` }}
                />
              </div>
              <span className="text-sm font-bold text-gray-900 w-10 text-right">
                {completionRate.toFixed(0)}%
              </span>
            </div>
            <span className="text-xs text-gray-500 inline-flex items-center gap-1">
              {t("statsNew.module.completionRate")}
              <InfoTooltip text={t("statsNew.module.tooltips.completionRate")} />
            </span>
          </div>

          {/* Average Progression */}
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${getCompletionColor(
                    avgProgressionPercent ?? 0,
                  )} transition-all duration-500`}
                  style={{
                    width: `${Math.min(avgProgressionPercent ?? 0, 100)}%`,
                  }}
                />
              </div>
              <span className="text-sm font-bold text-gray-900 w-10 text-right">
                {avgProgressionPercent != null ? `${avgProgressionPercent.toFixed(0)}%` : "-"}
              </span>
            </div>
            <span className="text-xs text-gray-500 inline-flex items-center gap-1">
              {t("statsNew.module.avgProgression")}
              <InfoTooltip text={t("statsNew.module.tooltips.avgProgression")} />
            </span>
          </div>
        </div>
      </div>

      {/* Secondary metrics */}
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-xs text-gray-500">{t("statsNew.module.avgDuration")}</span>
          <span className="text-sm font-semibold text-gray-700">
            {avgDuration != null ? formatDuration(avgDuration) : "-"}
          </span>
        </div>
        <div className="w-px h-4 bg-gray-200" />
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
          <span className="text-xs text-gray-500">{t("statsNew.module.abandonedSessions")}</span>
          <span className="text-sm font-semibold text-gray-700">{abandonedSessions}</span>
        </div>
      </div>
    </div>
  );
};

export default EngagementCard;
